import { FC, MouseEvent, useCallback, useEffect, useState } from 'react';
import { SortingUpIcon } from '../icons';
import { LoadingIndicator } from '../loading-indicator';
import {
  SortingIndicatorStyled,
  TableBodyStyled,
  TableHeadStyled,
  TableStyled,
  TableWrapperStyled,
} from './table.styled';
import {
  ERowColor,
  ESort,
  ISorting,
  ISortingIndicatorProps,
  ITableProps,
} from './table.types';

const SortingIndicator: FC<ISortingIndicatorProps> = ({ order }) => {
  return (
    <SortingIndicatorStyled $order={order}>
      <SortingUpIcon />
    </SortingIndicatorStyled>
  );
};

export function Table<
  ItemType,
  S = string,
  C extends keyof ItemType & string = keyof ItemType & string,
>({
  columns,
  list,
  cellRender = {},
  isSortable = true,
  isLoading,
  isSticky = false,
  stickyOffset = '0',
  onRowClick,
  onSort = () => {},
  defaultCellRender = (item, key) => <>{item[key as C]}</>,
  rowColor = () => ERowColor.GRAY,
}: ITableProps<ItemType, C, S>) {
  const renderCell = useCallback(
    (item: ItemType, key: C | string) => {
      const handler = cellRender[key];
      if (typeof handler === 'function') {
        return handler(item);
      } else {
        return defaultCellRender(item, key);
      }
    },
    [cellRender, defaultCellRender],
  );

  const [sorting, setSorting] = useState<ISorting<S> | null>(null);

  useEffect(() => {
    if (isSortable) {
      onSort(sorting);
    }
  }, [onSort, sorting, isSortable]);

  const thClickHandler = useCallback(
    (sortingValue?: S) => {
      if (!isSortable || !sortingValue) {
        return;
      }

      let mode: ESort | null;

      if (!sorting || sorting.value !== sortingValue) {
        mode = ESort.ASC;
      } else if (sorting.mode === ESort.ASC) {
        mode = ESort.DESC;
      } else {
        mode = null;
      }

      setSorting(
        mode
          ? {
              value: sortingValue,
              mode,
            }
          : null,
      );
    },
    [sorting, isSortable],
  );

  const rowClickHandler = useCallback(
    (item: ItemType, e: MouseEvent) => {
      if (onRowClick) {
        onRowClick(item, e);
      }
    },
    [onRowClick],
  );

  return (
    <TableWrapperStyled>
      <TableStyled>
        <TableHeadStyled $stickyOffset={stickyOffset} $isSticky={isSticky}>
          <tr>
            {columns.map(col => (
              <th
                key={col.key}
                onClick={() => thClickHandler(col.sortingValue)}
                style={{ width: col.width }}
              >
                <div className="col-head">
                  <span className="col-caption">{col.caption}</span>
                  {isSortable &&
                    sorting &&
                    sorting.value === col.sortingValue && (
                      <SortingIndicator order={sorting.mode} />
                    )}
                </div>
              </th>
            ))}
          </tr>
        </TableHeadStyled>
        {!isLoading && (
          <TableBodyStyled>
            {list.map((item, i) => (
              <tr
                key={i}
                className={rowColor(item)}
                onClick={e => rowClickHandler(item, e)}
              >
                {columns.map(({ key, width }) => (
                  <td key={key} width={width}>
                    {renderCell(item, key)}
                  </td>
                ))}
              </tr>
            ))}
          </TableBodyStyled>
        )}
      </TableStyled>
      {isLoading && <LoadingIndicator type="full" />}
    </TableWrapperStyled>
  );
}
